import { useFormContext } from 'react-hook-form';
import ReactQuill, { Quill } from 'react-quill';
import QuillResizeImage from 'quill-resize-image';
import QuillImageDropAndPaste from 'quill-image-drop-and-paste';
import 'react-quill/dist/quill.snow.css';
import './TextEditor.scss';
import { useRef } from 'react';
import CustomToolbar, { formats } from './CustomToolbar/CustomToolbar';

export type TextEditorProps = {
  label?: string;
  name: string;
  placeholder: string;
  isDisabled?: boolean;
  'data-testid'?: string;
  isRequired?: boolean;
  onSubmit: (e?: React.BaseSyntheticEvent) => void;
  isModal: boolean;
};

export const TextEditor = ({
  name,
  placeholder,
  isDisabled,
  'data-testid': dataTestId,
  label,
  isRequired,
  onSubmit,
  isModal,
}: TextEditorProps) => {
  const editorRef = useRef<ReactQuill>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const toolbarId = isModal ? 'custom-toolbar-modal' : 'custom-toolbar';
  if (typeof globalThis.window !== 'undefined' && Quill) {
    if (!Quill.imports['modules/resize']) {
      Quill.register('modules/resize', QuillResizeImage);
    }
    if (!Quill.imports['modules/imageDropAndPaste']) {
      Quill.register('modules/imageDropAndPaste', QuillImageDropAndPaste);
    }
  }
  const { setValue, watch } = useFormContext();
  const fieldValue = watch(name) ?? '';

  const handleFocus = () => {
    if (containerRef.current) {
      containerRef.current.classList.remove('blurred-editor');
      containerRef.current.classList.add('focused-editor');
    }
  };

  const handleBlur = (relatedEl: Element | null) => {
    const el = relatedEl;
    const isMovingToToolbar =
      !!el &&
      (!!el.closest('.ql-toolbar') ||
        !!el.closest('#custom-toolbar') ||
        el.classList?.contains('ql-picker-item') ||
        el.classList?.contains('ql-picker-label'));

    if (!isMovingToToolbar && containerRef.current) {
      containerRef.current.classList.remove('focused-editor');
      containerRef.current.classList.add('blurred-editor');
    }
  };

  const handleContainerClick = (event: React.MouseEvent) => {
    const target = event.target as Element;
    const isToolbarClick = target.closest('.ql-toolbar') || target.closest('#custom-toolbar');
    const isImageClick = target.tagName.toLowerCase() === 'img' || target.closest('img');
    const isImageResizeHandle = target.closest('.handler') || target.closest('.ql-container');

    if ((isToolbarClick || isImageClick || isImageResizeHandle) && containerRef.current) {
      containerRef.current.classList.remove('blurred-editor');
      containerRef.current.classList.add('focused-editor');
    }
  };

  return (
    <div
      ref={containerRef}
      className="blurred-editor text-editor"
      onClick={handleContainerClick}
      onBlur={(e) => handleBlur(e.relatedTarget || document.activeElement)}
      role="region"
      tabIndex={0}
      aria-label={label || 'Text editor'}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleContainerClick(e as unknown as React.MouseEvent<HTMLDivElement>);
        }
      }}
    >
      <ReactQuill
        ref={editorRef}
        theme="snow"
        value={fieldValue ?? ''}
        onChange={(value) => {
          return setValue(name, value, { shouldDirty: true, shouldTouch: true });
        }}
        onFocus={handleFocus}
        onBlur={() => {
          fieldValue.onBlur();
          handleBlur(document.activeElement);
        }}
        placeholder={placeholder}
        modules={{
          toolbar: {
            container: `#${toolbarId}`,
          },
          resize: {
            locale: {},
          },
          imageDropAndPaste: {},
        }}
        formats={formats}
        readOnly={isDisabled}
        data-testid={dataTestId}
      />
      <CustomToolbar
        name={name}
        isDisabled={isDisabled}
        placeholder={placeholder}
        label={label}
        isRequired={isRequired}
        onSubmit={onSubmit}
        data-testid="custom-toolbar"
        isModal={isModal}
        id={toolbarId}
      />
    </div>
  );
};
